import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {MachineDetailsService} from '@services/dashboard/machine-details.service';
import {Signal} from '@models/dashboard/signal';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {DashboardConfiguration} from '@models/dashboard/dashboard-configuration';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {btou, utob} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-dashboard-machine-layout',
    templateUrl: './machine-layout.component.html',
    styleUrls: ['./machine-layout.component.scss'],
    standalone: false
})
export class MachineLayoutComponent implements OnInit {
  @Input()
  machine: DashboardMachine = new DashboardMachine();
  @Input()
  isAdminOrSuperAdmin: boolean = false;
  @Input()
  configuration: DashboardConfiguration = new DashboardConfiguration();
  @Input()
  machineIcon: string = '';
  showContents: boolean = false;
  machineName: string = '';
  machineType: string = '';
  signals: Signal[] = [];
  selectedFileName: string = '';
  private MACHINE_STATUS_SIGNAL_NAME: string = 'MachineStatus';
  private NONE_SIGNAL_NAME: string = 'none';
  private FALLBACK_PLACEHOLDER_IMAGE: string = 'assets/img/common/fallback_img_placeholder.png';
  rawPlaceholderImage: string = this.FALLBACK_PLACEHOLDER_IMAGE;

  constructor(private activeModal: NgbActiveModal,
              private notificationService: NotificationService,
              private loadingIndicator: LoadingIndicatorService,
              private translationService: TranslateService,
              private machineDetailsService: MachineDetailsService,
              private sortingService: SortingService) {
  }

  ngOnInit(): void {
    this.showContents = false;
    // this.loadingIndicator.show();
    const machineDisplayProperties = this.machineDetailsService.getMachineDisplayProperties(this.machine);
    this.machineName = machineDisplayProperties.machineName;
    this.machineType = machineDisplayProperties.machineType;
    this.getSignals(this.machine);
    this.rawPlaceholderImage = this.getPlaceholderImageOrDefault(this.configuration);
  }


  onCloseModal() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onSaveChanges(): void {
    this.loadingIndicator.show();
    console.log('Saving configuration: ' + this.configuration);
    this.configuration = this.setDefaultTileValues(this.configuration, this.signals);
    if (this.machine == null || this.machine.id == undefined) {
      console.log('Invalid machine id. not saving dashboard details');
      return;
    }
    const machineId = this.machine.id;
    const machineType = this.machine.type;
    this.machineDetailsService.saveConfiguration(machineId, this.configuration).subscribe(() => {
      this.loadingIndicator.hide();
      let message = this.translationService.instant('The tile configuration for machine "#" was updated');
      message = message.replace('#', machineType);
      this.notificationService.success(message, 'MachineLayoutEdit', false);
      this.activeModal.dismiss('Save click');
    }, (error) => {
      this.loadingIndicator.hide();
      let message = this.translationService.instant('The tile configuration for machine "#" could not be updated');
      message = message.replace('#', machineType);
      this.notificationService.error(message, error, 'MachineLayoutEdit', true);
    });
  }

  onSignalClear(row: number, col: number) {
    this.configuration.dashboardRowsMap[row].itemsMap[col].signalName = this.NONE_SIGNAL_NAME;
  }

  onImageDelete() {
    this.rawPlaceholderImage = this.FALLBACK_PLACEHOLDER_IMAGE;
    this.configuration.placeholderImage = '';
    this.selectedFileName = '';
  }

  onImageChange($event: Event) {
    if (!$event.target) {
      return;
    }
    const target = $event.target as HTMLInputElement;
    if (!target || !target.files || target.files.length === 0) {
      return;
    }
    this.loadingIndicator.show();

    const file = target.files[0];

    if (!file) {
      this.loadingIndicator.hide();
      return;
    }

    if (!this.isImageFile(file)) {
      const message = this.translationService.instant('file is not an image');
      this.notificationService.error(file.name + ' ' + message, null, 'MachineLayoutComponent', true);
      this.loadingIndicator.hide();
      return;
    }

    this.selectedFileName = file.name;


    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      this.rawPlaceholderImage = reader.result as string;
      this.configuration.placeholderImage = btou(String(reader.result));
      this.loadingIndicator.hide();
    };

  }

  public onSignalChange(row: number, col: number, signal: Signal) {
    const configCell = this.configuration.dashboardRowsMap[row].itemsMap[col];
    if (configCell) {
      configCell.signalName = signal.name;
      configCell.decimals = signal.decimals;
      configCell.value = signal.value;
      configCell.label = signal.label;
      configCell.scale = signal.scale;
      configCell.unit = signal.unit;
      configCell.type = signal.type;
      configCell.isByteArray = signal.isByteArray;
    }
  }

  private isImageFile(file: File): boolean {
    const fileType = file.type;

    return fileType.includes('image');
  }

  private getSignals(machine: DashboardMachine) {
    this.signals.push(new Signal('None', 'NONE', this.NONE_SIGNAL_NAME, 'NONE'));
    this.signals.push(new Signal('MachineStatus', 'MACHINE STATUS', this.MACHINE_STATUS_SIGNAL_NAME, 'MACHINESTATUS'));

    if (machine === null || machine === undefined) {
      return;
    }

    this.machineDetailsService.getSignals(machine.id).subscribe({
      next: (signals: Signal[]) => {
        if (signals === null || !signals || signals.length === 0) {
          return;
        }
        const sortedSignals: Signal[] = Object.values(signals).sort((a, b) => this.sortingService.sortByName(a, b));

        for (const signal of sortedSignals) {
          this.signals = [...this.signals, signal];
        }
        this.configuration = this.setDefaultTileValues(this.configuration, this.signals);
        this.loadingIndicator.hide();
        this.showContents = true;

      }, error: (error) => {
        console.log('MachineTile LayoutConfig. Error getting list of signals', error);
        this.loadingIndicator.hide();
        this.showContents = true;
      }
    });

  }

  private getPlaceholderImageOrDefault(configuration: DashboardConfiguration): string {
    if (!configuration || !configuration.placeholderImage || configuration.placeholderImage.length === 0) {
      this.selectedFileName = '';
      return this.FALLBACK_PLACEHOLDER_IMAGE;
    }
    this.selectedFileName = ' ';
    return utob(this.configuration.placeholderImage);
  }

  private setDefaultTileValues(config: any, signals: Signal[]) {
    const TILE_LAYOUT_ROWS = 4;
    const TILE_LAYOUT_COLUMNS = 2;

    if (!config) {
      config = {
        id: -1,
        dashboardRowsMap: {},
        name: '',
        placeholderImage: ''
      };
    }

    if (!config.dashboardRowsMap) {
      config.dashboardRowsMap = {};
    }

    for (let rowIndex = 0; rowIndex < TILE_LAYOUT_ROWS; rowIndex++) {
      for (let colIndex = 0; colIndex < TILE_LAYOUT_COLUMNS; colIndex++) {
        if (!config.dashboardRowsMap[rowIndex]) {
          config.dashboardRowsMap[rowIndex] = {
            itemsMap: {}
          };
        }

        if (!config.dashboardRowsMap[rowIndex].itemsMap[colIndex] || !config.dashboardRowsMap[rowIndex].itemsMap[colIndex].signalName) {
          config.dashboardRowsMap[rowIndex].itemsMap[colIndex] = {signalName: this.NONE_SIGNAL_NAME};
        } else {
          if (config.dashboardRowsMap[rowIndex].itemsMap[colIndex].signalName !== this.MACHINE_STATUS_SIGNAL_NAME &&
              !this.signalExists(signals, config.dashboardRowsMap[rowIndex].itemsMap[colIndex].signalName)) {
            config.dashboardRowsMap[rowIndex].itemsMap[colIndex] = {signalName: this.NONE_SIGNAL_NAME};
          }
        }
      }
    }
    return config;
  }

  private signalExists(signals: Signal[], signalName: string): boolean {
    const upperCaseSignalName = signalName.toUpperCase();
    for (const s of signals) {
      if (s.name.toUpperCase() === upperCaseSignalName) {
        return true;
      }
    }
    return false;
  }
}
